import { useTranslation } from '@mukil/i18n';
import { Alert, Button } from '@jumpstone/ui-react';
import type { DirectoryEssNotice } from './employee_directory_ess';
import { ESS_ACTION_ERROR_KEY } from './ess_status_badge';

/**
 * The one dismissible outcome banner above the directory table for an
 * invite (auto-invite after "Add employee", or a row's Send / Resend /
 * Re-invite). `success` only when the email actually went; `warning` for
 * every case where the employee has NOT been notified yet.
 */
export function DirectoryEssNoticeBanner({
  notice,
  onDismiss,
}: {
  notice: DirectoryEssNotice;
  onDismiss: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const emailed = notice.kind === 'emailed';

  let title: string;
  if (emailed) {
    title = notice.after_create
      ? t('employees.ess_added_and_invited_title')
      : t('employees.ess_invite_emailed_title');
  } else {
    title = notice.after_create
      ? t('employees.ess_added_not_invited_title')
      : t('employees.ess_invite_failed_title');
  }

  let body: string;
  switch (notice.kind) {
    case 'emailed':
      body = t('employees.ess_directory_invite_emailed', {
        email: notice.email,
      });
      break;
    case 'email_failed':
      body = t('employees.ess_directory_email_failed', {
        email: notice.email,
      });
      break;
    case 'failed':
      body = t(ESS_ACTION_ERROR_KEY[notice.failure]);
      break;
  }

  return (
    <Alert
      variant={emailed ? 'success' : 'warning'}
      title={title}
      className="mb-3"
      data-testid="directory-ess-notice"
    >
      <span className="flex flex-wrap items-center justify-between gap-2">
        <span>{body}</span>
        <Button
          variant="ghost"
          size="xs"
          onClick={onDismiss}
          data-testid="directory-ess-notice-dismiss"
        >
          {t('common.close')}
        </Button>
      </span>
    </Alert>
  );
}
